/**
 * OceanPatch — the GPU half of the one-patch FFT ocean (shader gaps R5, 2026-09-30). WebGPU only (TSL compute + storage
 * textures); the pure math it runs is ocean_patch_math.ts, whose `emulatePipeline` is this class texel for texel.
 *
 * One compute PASS a frame (three's `renderer.compute(list)` records the whole list into one pass; WebGPU makes each
 * dispatch its own usage scope, so a texture written by one dispatch is read by the next):
 *   copy     displacement → displacementPrev (the previous frame's surface — THE positionPrevious LAW: the vertex
 *            stage emits the previous position from it, so motion vectors carry the waves' real one-frame motion)
 *   evolve   h(k,t) from the blended h0 (mix(A, B, w) — the cross-fade) and the per-texel ω/k/band texture; the ten real
 *            fields packed as five complex spectra in three RGBA32F textures (see ocean_patch_math.evolveTexel)
 *   passes   2·log2(N) radix-2 butterfly passes over all three textures (fft-pure's table, rows then columns)
 *   unpack   the (−1)^(x+y) recentre → three RGBA16F SAMPLED textures (linear, repeat):
 *              displacement = (dx, dy, dz, 0)        — the VERTEX band (the mesh's geometry)
 *              derivatives  = (sxF, szF, sxL, szL)   — slopes of the whole normal band / of the vertex band
 *              jacobian     = (jxx, jzz, jxz, hF)    — the horizontal map's derivatives (whole band) + the band's height
 * The first dispatch appends a copy so the first frame's previous surface is the current one (no bogus first-frame
 * velocity); a dispatch at an unchanged time runs the copy alone (a frozen sea has zero velocity).
 */

import * as THREE from 'three/webgpu';
import { Fn, cos, float, instanceIndex, int, ivec2, mix, sin, sqrt, step, textureLoad, textureStore, uniform, vec4 } from 'three/tsl';
import { generateButterfly } from './fft-pure.js';
import type { PatchGrid } from './ocean_patch_math.js';

type ComputeNode = { compute(count: number): unknown };
/** The slice of three's WebGPURenderer the patch drives. */
export interface ComputeTarget { compute(nodes: unknown): unknown }

export class OceanPatch {
  readonly displacement: THREE.StorageTexture;
  readonly displacementPrev: THREE.StorageTexture;
  readonly derivatives: THREE.StorageTexture;
  readonly jacobian: THREE.StorageTexture;
  /** The cross-fade weight (h0 = mix(A, B, w)). */
  readonly uBlend = uniform(0);
  /** The evolve kernel's clock (s). */
  readonly uTime = uniform(0);
  /** Horizontal chop λ. */
  readonly uChop: ReturnType<typeof uniform<number>>;
  readonly stats = { dispatches: 0, copies: 0, frames: 0 };

  private readonly texA: THREE.DataTexture;
  private readonly texB: THREE.DataTexture;
  private readonly texOmega: THREE.DataTexture;
  private readonly texButterfly: THREE.DataTexture;
  private readonly ping: THREE.StorageTexture[];
  private readonly pong: THREE.StorageTexture[];
  private readonly fullList: unknown[];
  private readonly firstList: unknown[];
  private readonly copyList: unknown[];
  private primed = false;
  private disposed = false;

  constructor(private readonly target: ComputeTarget, readonly grid: PatchGrid, h0A: Float32Array, h0B: Float32Array = h0A) {
    const N = grid.N;
    this.uChop = uniform(grid.chop);
    this.texA = dataTexture(h0A.slice(), N, N);
    this.texB = dataTexture(h0B.slice(), N, N);
    this.texOmega = dataTexture(grid.omegaData.slice(), N, N);
    const { data: bf, stages } = generateButterfly(N);
    this.texButterfly = dataTexture(bf, N, stages);
    this.ping = [storageTex(N, false), storageTex(N, false), storageTex(N, false)];
    this.pong = [storageTex(N, false), storageTex(N, false), storageTex(N, false)];
    this.displacement = storageTex(N, true);
    this.displacementPrev = storageTex(N, true);
    this.derivatives = storageTex(N, true);
    this.jacobian = storageTex(N, true);
    this.displacement.name = 'OceanPatchDisplacement';
    this.displacementPrev.name = 'OceanPatchDisplacementPrev';
    this.derivatives.name = 'OceanPatchDerivatives';
    this.jacobian.name = 'OceanPatchJacobian';

    const count = N * N;
    const texA = this.texA, texB = this.texB, texO = this.texOmega, texBf = this.texButterfly;
    const uBlend = this.uBlend, uTime = this.uTime, uChop = this.uChop;
    const disp = this.displacement, prev = this.displacementPrev;
    const P0 = this.ping, deriv = this.derivatives, jac = this.jacobian;

    const texel = (): { coord: ReturnType<typeof ivec2>; x: ReturnType<typeof int>; y: ReturnType<typeof int> } => {
      const idx = int(instanceIndex);
      const x = idx.mod(N);
      const y = idx.div(N);
      return { coord: ivec2(x, y), x, y };
    };

    // ── copy: the previous frame's surface ──
    const copy = Fn(() => {
      const { coord } = texel();
      textureStore(prev, coord, textureLoad(disp, coord));
    })() as unknown as ComputeNode;

    // ── evolve ──
    const evolve = Fn(() => {
      const { coord } = texel();
      const h0 = mix(textureLoad(texA, coord), textureLoad(texB, coord), uBlend); // THE MIX LAW: the function form
      const om = textureLoad(texO, coord);
      const w = om.x, kx = om.y, kz = om.z;
      const mV = step(1.5, om.w), mN = step(0.5, om.w);
      const ph = w.mul(uTime);
      const c = cos(ph), s = sin(ph);
      const hRe = h0.x.mul(c).sub(h0.y.mul(s)).add(h0.z.mul(c)).sub(h0.w.mul(s));
      const hIm = h0.x.mul(s).add(h0.y.mul(c)).sub(h0.z.mul(s)).sub(h0.w.mul(c));
      const k = sqrt(kx.mul(kx).add(kz.mul(kz))).max(1e-6);
      const kxN = kx.div(k), kzN = kz.div(k);
      // i·f·h = (−f·hIm, f·hRe); f·h = (f·hRe, f·hIm). The pack C = F + i·G = (F.re − G.im, F.im + G.re).
      const hvRe = hRe.mul(mV), hvIm = hIm.mul(mV);
      const lxv = uChop.mul(kxN).mul(mV), lzv = uChop.mul(kzN).mul(mV);
      const dxRe = lxv.mul(hIm).negate(), dxIm = lxv.mul(hRe);
      const dzRe = lzv.mul(hIm).negate(), dzIm = lzv.mul(hRe);
      const sxfRe = kx.mul(hIm).negate().mul(mN), sxfIm = kx.mul(hRe).mul(mN);
      const szfRe = kz.mul(hIm).negate().mul(mN), szfIm = kz.mul(hRe).mul(mN);
      const sxlRe = kx.mul(hIm).negate().mul(mV), sxlIm = kx.mul(hRe).mul(mV);
      const szlRe = kz.mul(hIm).negate().mul(mV), szlIm = kz.mul(hRe).mul(mV);
      const fxx = uChop.negate().mul(kx).mul(kxN).mul(mN);
      const fzz = uChop.negate().mul(kz).mul(kzN).mul(mN);
      const fxz = uChop.negate().mul(kz).mul(kxN).mul(mN);
      const jxxRe = fxx.mul(hRe), jxxIm = fxx.mul(hIm);
      const jzzRe = fzz.mul(hRe), jzzIm = fzz.mul(hIm);
      const jxzRe = fxz.mul(hRe), jxzIm = fxz.mul(hIm);
      const hfRe = hRe.mul(mN), hfIm = hIm.mul(mN);
      textureStore(P0[0], coord, vec4(hvRe.sub(dxIm), hvIm.add(dxRe), dzRe.sub(sxfIm), dzIm.add(sxfRe)));
      textureStore(P0[1], coord, vec4(szfRe.sub(sxlIm), szfIm.add(sxlRe), szlRe.sub(jxxIm), szlIm.add(jxxRe)));
      textureStore(P0[2], coord, vec4(jzzRe.sub(jxzIm), jzzIm.add(jxzRe), hfRe, hfIm));
    })() as unknown as ComputeNode;

    // ── butterfly passes (rows, then columns; bindings baked per pass — the ReSTIR even/odd precedent) ──
    const passes: unknown[] = [];
    let src = this.ping, dst = this.pong;
    for (let axis = 0; axis < 2; axis++) {
      for (let stage = 0; stage < stages; stage++) {
        const s0 = src, d0 = dst;
        const horizontal = axis === 0;
        const pass = Fn(() => {
          const { coord, x, y } = texel();
          const bfv = textureLoad(texBf, ivec2(horizontal ? x : y, int(stage)));
          const ia = int(bfv.z), ib = int(bfv.w);
          const cA = horizontal ? ivec2(ia, y) : ivec2(x, ia);
          const cB = horizontal ? ivec2(ib, y) : ivec2(x, ib);
          const wr = bfv.x, wi = bfv.y;
          for (let t = 0; t < 3; t++) {
            const A = textureLoad(s0[t], cA), B = textureLoad(s0[t], cB);
            textureStore(d0[t], coord, vec4(
              A.x.add(wr.mul(B.x).sub(wi.mul(B.y))),
              A.y.add(wr.mul(B.y).add(wi.mul(B.x))),
              A.z.add(wr.mul(B.z).sub(wi.mul(B.w))),
              A.w.add(wr.mul(B.w).add(wi.mul(B.z))),
            ));
          }
        })() as unknown as ComputeNode;
        passes.push(pass.compute(count));
        const tmp = src; src = dst; dst = tmp;
      }
    }
    // 2·stages passes (even) ⇒ the transformed field is back in `ping`.
    const fin = src;

    // ── unpack ──
    const unpack = Fn(() => {
      const { coord, x, y } = texel();
      const sign = float(1).sub(float(x.add(y).mod(2)).mul(2));
      const a = textureLoad(fin[0], coord).mul(sign);
      const b = textureLoad(fin[1], coord).mul(sign);
      const c = textureLoad(fin[2], coord).mul(sign);
      textureStore(disp, coord, vec4(a.y, a.x, a.z, 0));
      textureStore(deriv, coord, vec4(a.w, b.x, b.y, b.z));
      textureStore(jac, coord, vec4(b.w, c.x, c.y, c.z));
    })() as unknown as ComputeNode;

    const copyNode = copy.compute(count);
    const pipeline = [evolve.compute(count), ...passes, unpack.compute(count)];
    this.fullList = [copyNode, ...pipeline];
    this.firstList = [...pipeline, copyNode];
    this.copyList = [copyNode];
  }

  /** Replace the running spectrum A / the incoming spectrum B (one upload each — a sea-state change, never per frame). */
  setSpectrumA(h0: Float32Array): void { (this.texA.image.data as Float32Array).set(h0); this.texA.needsUpdate = true; }
  setSpectrumB(h0: Float32Array): void { (this.texB.image.data as Float32Array).set(h0); this.texB.needsUpdate = true; }

  /** One frame: the full pipeline at `time` (or only the previous-surface copy when the time did not move). */
  dispatch(time: number, blend: number, advance: boolean): void {
    if (this.disposed) return;
    this.uTime.value = time;
    this.uBlend.value = blend;
    const list = !this.primed ? this.firstList : advance ? this.fullList : this.copyList;
    this.primed = true;
    this.target.compute(list);
    this.stats.dispatches += list.length;
    this.stats.frames++;
    if (list === this.copyList) this.stats.copies++;
  }

  /** The number of compute dispatches a full frame runs (copy + evolve + passes + unpack). */
  get dispatchesPerFrame(): number { return this.fullList.length; }

  dispose(): void {
    this.disposed = true;
    for (const t of [this.texA, this.texB, this.texOmega, this.texButterfly, ...this.ping, ...this.pong, this.displacement, this.displacementPrev, this.derivatives, this.jacobian]) t.dispose();
  }
}

function dataTexture(data: Float32Array, width: number, height: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Intermediates are RGBA32F read with textureLoad (precision through 2·log2 N passes; unfilterable, no sampler); the
 * sampled outputs RGBA16F (linearly filterable everywhere — float32 filtering is an optional feature), repeat-wrapped.
 */
function storageTex(N: number, sampled: boolean): THREE.StorageTexture {
  const tex = new THREE.StorageTexture(N, N);
  tex.type = sampled ? THREE.HalfFloatType : THREE.FloatType;
  tex.format = THREE.RGBAFormat;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  if (sampled) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearFilter;
  } else {
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
  }
  return tex;
}
